"use client";

import { useCallback, useMemo } from "react";
import {
  useBlockNoteEditor,
  useComponentsContext,
  useSelectedBlocks,
} from "@blocknote/react";
import type {
  BlockSchema,
  InlineContentSchema,
  StyleSchema,
} from "@blocknote/core";
import type { VerticalAlignment } from "../extensions/VerticalAlignmentExtension";
import { getSelectedCellPositions } from "../utils/prosemirror-table-utils";

const icons: Record<VerticalAlignment, JSX.Element> = {
  top: (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
      <rect x="2" y="2" width="12" height="12" rx="1" />
      <line x1="5" y1="5" x2="11" y2="5" />
    </svg>
  ),
  middle: (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
      <rect x="2" y="2" width="12" height="12" rx="1" />
      <line x1="5" y1="8" x2="11" y2="8" />
    </svg>
  ),
  bottom: (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
      <rect x="2" y="2" width="12" height="12" rx="1" />
      <line x1="5" y1="11" x2="11" y2="11" />
    </svg>
  ),
};

const tooltips: Record<VerticalAlignment, string> = {
  top: "위쪽 정렬",
  middle: "세로 가운데 정렬",
  bottom: "아래쪽 정렬",
};

function getCurrentVerticalAlignment(editor: any): VerticalAlignment | undefined {
  const tiptap = editor._tiptapEditor;
  if (!tiptap) return undefined;

  const positions = getSelectedCellPositions(editor);
  if (positions.length === 0) return undefined;

  const { state } = tiptap;
  const alignments = positions.map((pos) => {
    const node = state.doc.nodeAt(pos);
    return (node?.attrs?.verticalAlignment as VerticalAlignment) || "top";
  });

  const first = alignments[0];
  return alignments.every((a) => a === first) ? first : undefined;
}

export const VerticalAlignButton = (props: {
  verticalAlignment: VerticalAlignment;
}) => {
  const Components = useComponentsContext()!;
  const editor = useBlockNoteEditor<
    BlockSchema,
    InlineContentSchema,
    StyleSchema
  >();

  const selectedBlocks = useSelectedBlocks(editor);

  const currentAlignment = useMemo(() => {
    return getCurrentVerticalAlignment(editor);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor, selectedBlocks]);

  const setVerticalAlignment = useCallback(
    (alignment: VerticalAlignment) => {
      const tiptap = editor._tiptapEditor;
      if (!tiptap) return;

      const positions = getSelectedCellPositions(editor);
      if (positions.length === 0) return;

      editor.focus();

      const { state } = tiptap;
      let tr = state.tr;

      for (const pos of positions) {
        const node = tr.doc.nodeAt(pos);
        if (node) {
          tr = tr.setNodeMarkup(pos, undefined, {
            ...node.attrs,
            verticalAlignment: alignment,
          });
        }
      }

      tiptap.view?.dispatch(tr);
    },
    [editor],
  );

  const isInTable = useMemo(() => {
    return selectedBlocks.some((block) => block.type === "table");
  }, [selectedBlocks]);

  if (!isInTable || !editor.isEditable) {
    return null;
  }

  return (
    <Components.FormattingToolbar.Button
      className={"bn-button"}
      data-test={`verticalAlign${props.verticalAlignment.charAt(0).toUpperCase() + props.verticalAlignment.slice(1)}`}
      onClick={() => setVerticalAlignment(props.verticalAlignment)}
      isSelected={currentAlignment === props.verticalAlignment}
      label={tooltips[props.verticalAlignment]}
      mainTooltip={tooltips[props.verticalAlignment]}
      icon={icons[props.verticalAlignment]}
    />
  );
};
